/**
 * Título y descripción SEO de un producto o una categoría (ADM-08, SEO-04).
 *
 * El mismo formulario sirve para las dos entidades: el contrato que se guarda
 * es idéntico (`ActualizarSeoDto`), solo cambia a dónde lo manda quien lo usa.
 * Los dos campos son opcionales — vacíos, la ficha usa el nombre y una
 * descripción generada (`FichaProducto.tsx`, `Categoria.tsx`).
 *
 * Los límites (60 y 155 caracteres) son la referencia habitual de cuánto
 * Google no trunca en el resultado de búsqueda. No son un límite real de
 * Google —el corte es por píxeles, no por caracteres— así que el aviso es
 * solo eso, un aviso: nunca impide guardar.
 */
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';

import type { ActualizarSeoDto } from '@elcarpintero/compartido';

import { Icono } from '../comunes/Icono';
import { Modal } from './Modal';

const LARGO_TITULO = 60;
const LARGO_DESCRIPCION = 155;

/** Lo mínimo que el formulario necesita de la entidad que se edita. */
export interface EntidadConSeo {
  tituloSeo: string | null;
  descripcionSeo: string | null;
}

export interface FormularioSeoProps {
  /** `null` mantiene el modal cerrado. */
  entidad: EntidadConSeo | null;
  titulo: string;
  bajada: string;
  placeholderTitulo: string;
  placeholderDescripcion: string;
  enviando: boolean;
  falla: string | null;
  onCerrar(): void;
  onGuardar(datos: ActualizarSeoDto): void;
}

function vacioANulo(valor: string): string | null {
  const limpio = valor.trim();
  return limpio.length > 0 ? limpio : null;
}

function Contador({ id, largo, limite }: { id: string; largo: number; limite: number }) {
  const excedido = largo > limite;
  return (
    <p id={id} className={excedido ? 'campo-ayuda excedido' : 'campo-ayuda'} role="status">
      {largo}/{limite} caracteres
      {excedido ? ' · Google probablemente lo corte en el resultado de búsqueda' : ''}
    </p>
  );
}

export function FormularioSeo({
  entidad,
  titulo,
  bajada,
  placeholderTitulo,
  placeholderDescripcion,
  enviando,
  falla,
  onCerrar,
  onGuardar,
}: FormularioSeoProps) {
  const [tituloSeo, setTituloSeo] = useState('');
  const [descripcionSeo, setDescripcionSeo] = useState('');

  // Al abrir se carga lo que ya tiene la entidad. `entidad` es la referencia
  // que guarda la pantalla al elegir que editar, asi que no cambia mientras
  // el modal sigue abierto (no se resetea el formulario a medio escribir).
  useEffect(() => {
    if (entidad === null) return;
    setTituloSeo(entidad.tituloSeo ?? '');
    setDescripcionSeo(entidad.descripcionSeo ?? '');
  }, [entidad]);

  function enviar(evento: FormEvent<HTMLFormElement>): void {
    evento.preventDefault();
    onGuardar({ tituloSeo: vacioANulo(tituloSeo), descripcionSeo: vacioANulo(descripcionSeo) });
  }

  return (
    <Modal
      titulo={titulo}
      bajada={bajada}
      abierto={entidad !== null}
      onCerrar={() => {
        if (!enviando) onCerrar();
      }}
    >
      <form className="formulario-admin" onSubmit={enviar} noValidate>
        {falla !== null ? (
          <p className="acceso-error" role="alert">
            <Icono nombre="alerta" />
            <span>{falla}</span>
          </p>
        ) : null}

        <div className="campo">
          <label htmlFor="seo-titulo">Título SEO (opcional)</label>
          <input
            id="seo-titulo"
            autoComplete="off"
            placeholder={placeholderTitulo}
            aria-describedby="ayuda-seo-titulo"
            value={tituloSeo}
            onChange={(evento) => {
              setTituloSeo(evento.target.value);
            }}
          />
          <Contador id="ayuda-seo-titulo" largo={tituloSeo.length} limite={LARGO_TITULO} />
        </div>

        <div className="campo">
          <label htmlFor="seo-descripcion">Descripción SEO (opcional)</label>
          <textarea
            id="seo-descripcion"
            rows={3}
            placeholder={placeholderDescripcion}
            aria-describedby="ayuda-seo-descripcion"
            value={descripcionSeo}
            onChange={(evento) => {
              setDescripcionSeo(evento.target.value);
            }}
          />
          <Contador id="ayuda-seo-descripcion" largo={descripcionSeo.length} limite={LARGO_DESCRIPCION} />
        </div>

        <div className="modal-acciones">
          <button type="button" className="btn btn-contorno" onClick={onCerrar} disabled={enviando}>
            Cancelar
          </button>
          <button type="submit" className="btn btn-primario" disabled={enviando}>
            {enviando ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
